import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { MdMoreVert, MdDeleteForever, MdEdit, MdAdd, MdExpandMore, MdExpandLess, MdCheck, MdOutlineNoteAdd } from "react-icons/md";
import { Menu } from "@headlessui/react";
import api from "@/services/api";

export default ({ answer, setAnswer }) => {
  const [notes, setNotes] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState({
    description: "",
    title: "",
    category: "",
    status: "todo",
    mission_ao_answer_id: answer?._id,
  });

  useEffect(() => {
    fetchNotes();
  }, [answer]);

  useEffect(() => {
    // Set all categories expanded by default
    const categories = [...new Set(notes.map((note) => note.category))];
    const defaultExpanded = {};
    categories.forEach((category) => {
      defaultExpanded[category] = true;
    });
    setExpandedCategories(defaultExpanded);
  }, [notes]);

  async function fetchNotes() {
    try {
      const { ok, data } = await api.post("/mission_ao_epic/search", { mission_ao_answer_id: answer._id });
      if (!ok) throw new Error("Failed to fetch notes");
      setNotes(data);
    } catch (error) {
      console.error(error);
      toast.error("Error loading notes");
    }
  }

  const toggleStatus = async (note, event) => {
    event.stopPropagation();
    const newStatus = note.status === "done" ? "todo" : "done";
    try {
      await api.put(`/mission_ao_epic/${note._id}`, { status: newStatus });
      setNotes((prevNotes) => prevNotes.map((n) => (n._id === note._id ? { ...n, status: newStatus } : n)));
      toast.success(newStatus === "done" ? "Note marquée comme traitée" : "Note marquée comme à faire");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    try {
      const { ok, data } = await api.post("/mission_ao_epic", newNote);
      if (!ok) throw new Error("Failed to create note");
      setNotes([...notes, data]);
      setIsAddingNote(false);
      setNewNote({
        description: "",
        title: "",
        category: "",
        status: "todo",
        mission_ao_answer_id: answer?._id,
      });
      toast.success("Note added successfully");
    } catch (error) {
      console.error(error);
      toast.error("Error creating note");
    }
  };

  const categories = [...new Set(notes.map((note) => note.category))];

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const filteredNotes = notes.filter(
    (note) =>
      note.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.category?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="p-4 relative bg-white rounded-lg shadow">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Notes & Suivi de l'Appel d'Offres</h2>
        <p className="text-gray-600">Centralisez et organisez toutes vos observations et points importants</p>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="relative w-1/2">
          <input
            type="text"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Rechercher une note..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
        </div>
        <button onClick={() => setIsAddingNote(true)} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
          <MdOutlineNoteAdd className="mr-2" /> Ajouter une Note
        </button>
      </div>

      {isAddingNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Ajouter une nouvelle note</h2>
            <form onSubmit={handleAddNote}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Note</label>
                  <textarea
                    rows={2}
                    value={newNote.description}
                    onChange={(e) => setNewNote({ ...newNote, description: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Saisissez une note concise"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Commentaire détaillé</label>
                  <textarea
                    rows={5}
                    value={newNote.title}
                    onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Ajoutez des détails, contexte ou actions à entreprendre"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Catégorie</label>
                  <input
                    type="text"
                    value={newNote.category}
                    onChange={(e) => setNewNote({ ...newNote, category: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="ex: questions_client, points_techniques, documents"
                    required
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button type="button" onClick={() => setIsAddingNote(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
                  Annuler
                </button>
                <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <EditNoteModal
        note={editingNote}
        setNote={async (n) => {
          if (!n) return setEditingNote(null);
          try {
            await api.put(`/mission_ao_epic/${editingNote._id}`, { ...n });
            fetchNotes();
            toast.success("Note mise à jour avec succès!");
            setEditingNote(null);
          } catch (error) {
            console.error(error);
            toast.error("Erreur lors de la mise à jour de la note");
          }
        }}
      />

      {notes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <MdOutlineNoteAdd className="text-gray-400 w-16 h-16 mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">Aucune note pour cet appel d'offres</h3>
          <p className="text-gray-500 mb-4 text-center max-w-md">Ajoutez des notes pour suivre les points importants, les questions et les documents nécessaires.</p>
          <button onClick={() => setIsAddingNote(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
            Créer ma première note
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto mt-4">
          <table className="w-full bg-white rounded-lg shadow-sm table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">Note</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">Détails</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Statut</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {categories.map((category) => {
                const categoryNotes = filteredNotes.filter((note) => note.category === category);
                if (categoryNotes.length === 0) return null;

                return (
                  <React.Fragment key={category}>
                    <tr className="bg-gray-50 cursor-pointer" onClick={() => toggleCategory(category)}>
                      <td colSpan={4} className="px-6 py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            {expandedCategories[category] ? <MdExpandLess className="w-5 h-5 mr-2" /> : <MdExpandMore className="w-5 h-5 mr-2" />}
                            <span className="font-medium capitalize">{category?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) || "Sans catégorie"}</span>
                          </div>
                          <span className="text-sm text-gray-500">
                            {categoryNotes.length} élément{categoryNotes.length > 1 ? "s" : ""}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {expandedCategories[category] &&
                      categoryNotes
                        .sort((a, b) => (a.status === "done" ? 1 : -1))
                        .map((note) => (
                          <tr key={note._id} className={`hover:bg-gray-50 ${note.status === "done" ? "bg-gray-50" : ""}`}>
                            <td className="px-6 py-4">
                              <div className={`${note.status === "done" ? "text-gray-500" : "text-gray-900"}`}>{note.description}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className={`${note.status === "done" ? "text-gray-500" : "text-gray-700"} whitespace-pre-wrap`}>{note.title}</div>
                            </td>
                            <td className="px-6 py-4 text-sm font-medium">
                              <button
                                onClick={(event) => toggleStatus(note, event)}
                                className={`inline-flex items-center px-3 py-1 rounded-full ${
                                  note.status === "done" ? "bg-green-100 text-green-800 hover:bg-green-200" : "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                                } transition-colors`}>
                                {note.status === "done" ? (
                                  <>
                                    <MdCheck className="mr-1" /> Traité
                                  </>
                                ) : (
                                  <>À traiter</>
                                )}
                              </button>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <Menu as="div" className="relative inline-block text-left">
                                <Menu.Button className="p-1.5 hover:bg-gray-100 rounded-full">
                                  <MdMoreVert className="text-gray-500 w-5 h-5" />
                                </Menu.Button>
                                <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 ring-1 ring-black ring-opacity-5">
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={`${active ? "bg-blue-50 text-blue-700" : "text-blue-600"} flex w-full items-center px-4 py-2 text-sm`}
                                        onClick={() => setEditingNote(note)}>
                                        <MdEdit className="mr-2 w-5 h-5" /> Modifier
                                      </button>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={`${active ? "bg-red-50 text-red-700" : "text-red-600"} flex w-full items-center px-4 py-2 text-sm`}
                                        onClick={async () => {
                                          try {
                                            if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette note ?")) return;
                                            const { ok } = await api.remove(`/mission_ao_epic/${note._id}`);
                                            if (!ok) throw new Error("Failed to delete note");
                                            fetchNotes();
                                            toast.success("Note supprimée avec succès !");
                                          } catch (error) {
                                            console.error(error);
                                            toast.error("Erreur lors de la suppression de la note");
                                          }
                                        }}>
                                        <MdDeleteForever className="mr-2 w-5 h-5" /> Supprimer
                                      </button>
                                    )}
                                  </Menu.Item>
                                </Menu.Items>
                              </Menu>
                            </td>
                          </tr>
                        ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const EditNoteModal = ({ note, setNote }) => {
  const [editedNote, setEditedNote] = useState(null);

  useEffect(() => {
    setEditedNote({ ...note });
  }, [note]);

  if (!note) return null;
  if (!editedNote) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Modifier la note</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setNote(editedNote);
          }}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Note</label>
              <textarea
                rows={2}
                value={editedNote.description}
                onChange={(e) => setEditedNote({ ...editedNote, description: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Commentaire détaillé</label>
              <textarea
                rows={5}
                value={editedNote.title}
                onChange={(e) => setEditedNote({ ...editedNote, title: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Catégorie</label>
              <input
                type="text"
                value={editedNote.category}
                onChange={(e) => setEditedNote({ ...editedNote, category: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button type="button" onClick={() => setNote(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
              Annuler
            </button>
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
              Sauvegarder
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
